<div class="flex flex-col p-8 gap-8" [style.height]="sheetHeight">
  @if (loading) {
    <app-loader></app-loader>
  }

  @if (editFormGroup && !loading) {
    <div class="flex flex-col">
      <h3 class="flex gap-4 items-center">
        Editing sheet
        <input
          class="input"
          matInput
          type="text"
          data-test-id="data-capture-edit-sheet-title"
          [formControl]="nameControl"
        />
      </h3>
      @if (editFormSubmitted) {
        <div
          class="title-error-container"
          data-test-id="data-capture-edit-sheet-title-errors"
        >
          @if (nameControl.hasError('required')) {
            <mat-error> Your sheet name is required </mat-error>
          }
          @if (nameControl.hasError('invalidName')) {
            <mat-error>
              Your sheet name must not contain the following characters [ ? : /
              [ \ ] * ]
            </mat-error>
          }
          @if (
            nameControl.hasError('minlength') ||
            nameControl.hasError('maxlength')
          ) {
            <mat-error>
              Your sheet name must be between 3 and 31 characters
            </mat-error>
          }
          @if (nameControl.hasError('duplicateName')) {
            <mat-error> Your sheet name already exists </mat-error>
          }
        </div>
      }
    </div>
    <form class="flex flex-row gap-16" [formGroup]="editFormGroup">
      <div
        class="flex flex-col basis-[50%] gap-4"
        [style.height]="questionHeight"
      >
        <app-reorderable-expansion-panels
          [showErrors]="editFormSubmitted"
          [suggestedQuestionsByTheme]="suggestedQuestionsByTheme"
          (approveSuggestion)="updateSuggestionStatus($event, 'approved')"
          (declineSuggestion)="updateSuggestionStatus($event, 'declined')"
        >
        </app-reorderable-expansion-panels>
        @if (editFormSubmitted) {
          @if (themesControl.hasError('required')) {
            <mat-error data-test-id="edit-sheet-error-theme-required">
              At least one section and question must be added
            </mat-error>
          }
          @if (themesControl.hasError('noThemeName')) {
            <mat-error data-test-id="edit-sheet-error-no-theme-name">
              Interview section name is required
            </mat-error>
          }
          @if (themesControl.hasError('noQuestions')) {
            <mat-error data-test-id="edit-sheet-error-no-questions">
              At least one question is required for every interview section
            </mat-error>
          }
          @if (themesControl.hasError('noQuestionName')) {
            <mat-error data-test-id="edit-sheet-error-no-question-name">
              Question name is required for all present questions
            </mat-error>
          }
          @if (themesControl.hasError('duplicateThemeNames')) {
            <mat-error data-test-id="edit-sheet-error-duplicate-theme-names">
              Interview section names must be unique
            </mat-error>
          }
          @if (themesControl.hasError('duplicateQuestionNames')) {
            <mat-error data-test-id="edit-sheet-error-duplicate-question-names">
              Question names must be unique within each interview section
            </mat-error>
          }
        }
      </div>
      <div
        class="flex flex-col basis-[50%] gap-8"
        [style.height]="questionHeight"
      >
        <div class="flex flex-col gap-4">
          <b>Segments</b>
          <mat-form-field>
            <mat-label
              >Which segment(s) should be linked to this sheet?</mat-label
            >
            <mat-select
              multiple
              formControlName="segments"
              data-test-id="edit-sheet-segment-selection-dropdown"
            >
              @for (segment of segments; track segment) {
                <mat-option
                  data-test-id="edit-sheet-segment-selection-option"
                  [value]="segment.segmentId"
                  [disabled]="
                    segment.occupied &&
                    !sheetSegmentIds.includes(segment.segmentId)
                  "
                >
                  {{ segment.segmentName }}
                </mat-option>
              }
            </mat-select>
            <mat-error data-test-id="edit-sheet-segment-selection-error">
              At least one segment must be selected
            </mat-error>
          </mat-form-field>
        </div>
        <div class="additional-fields-container flex flex-row gap-8">
          <div class="flex flex-col gap-2">
            <b>Optional fields</b>
            <mat-checkbox
              formControlName="geography"
              data-test-id="data-capture-edit-sheet-additional-fields-geography"
            >
              Geography
            </mat-checkbox>
            <mat-checkbox
              formControlName="profileType"
              data-test-id="data-capture-edit-sheet-additional-fields-profile-type"
            >
              Profile type
            </mat-checkbox>
            <mat-checkbox
              formControlName="dateAndTimeOfCall"
              data-test-id="data-capture-edit-sheet-additional-fields-date-and-time-of-call"
            >
              Date and time of call
            </mat-checkbox>
            <mat-checkbox
              formControlName="recruitmentSource"
              data-test-id="data-capture-edit-sheet-additional-fields-recruitment-source"
            >
              <div class="flex flex-row items-center content-center gap-1">
                <span>Recruitment source</span>
                <mat-icon
                  class="info"
                  matTooltip="Displays the source for experts added through file upload"
                >
                  info_outline
                </mat-icon>
              </div>
            </mat-checkbox>
          </div>
          <div class="flex flex-col gap-2">
            <b>Include in export</b>
            <mat-checkbox
              formControlName="expertBio"
              data-test-id="data-capture-edit-sheet-additional-fields-expert-biography"
            >
              Expert biography
            </mat-checkbox>
          </div>
        </div>
        <div class="flex flex-row gap-8">
          <div class="flex flex-col gap-4 basis-[50%]">
            <b>Answer type</b>
            <mat-radio-group
              formControlName="answerType"
              class="flex flex-row gap-1"
            >
              <mat-radio-button
                value="summarised"
                data-test-id="data-capture-edit-sheet-answer-format-summarized-option"
              >
                Summarized
              </mat-radio-button>
              <mat-radio-button
                value="verbatim"
                data-test-id="data-capture-edit-sheet-answer-format-verbatim-option"
              >
                Verbatim
              </mat-radio-button>
            </mat-radio-group>
            <div class="alert flex gap-2">
              <mat-icon class="info">info_outline</mat-icon>
              <span>
                Changing answer type will re-generate all responses for this
                sheet.
              </span>
            </div>
          </div>
          <div class="flex flex-col gap-4 basis-[50%]">
            <b>Suggested question threshold</b>
            <div class="flex flex-col gap-2">
              <mat-form-field>
                <mat-label>Threshold</mat-label>
                <input
                  matInput
                  type="number"
                  formControlName="suggestedQuestionThreshold"
                  min="2"
                  data-test-id="edit-sheet-suggested-question-threshold"
                />
                <mat-hint>
                  Interviews required before surfacing a suggestion
                </mat-hint>
                <mat-error>Minimum value is 2</mat-error>
              </mat-form-field>
              @if (loadingSuggestedCount) {
                <span class="text-sm text-gray-500"> Loading preview... </span>
              }
              @if (!loadingSuggestedCount && suggestedQuestionCount !== null) {
                <span class="text-sm text-gray-600">
                  {{ suggestedQuestionCount }} question{{
                    suggestedQuestionCount === 1 ? '' : 's'
                  }}
                  would be suggested at this threshold
                </span>
              }
            </div>
          </div>
        </div>
      </div>
    </form>
    <div class="flex flex-row justify-between mt-auto">
      <button
        mat-button
        data-test-id="data-capture-edit-sheet-cancel-button"
        [disabled]="submitting"
        (click)="close()"
      >
        Cancel
      </button>
      <button
        mat-button
        data-test-id="data-capture-edit-sheet-publish-button"
        [disabled]="submitting"
        (click)="confirmChange()"
      >
        {{ submitting ? 'Saving changes...' : 'Save changes' }}
      </button>
    </div>
  }
</div>
